import React, { useEffect, useRef, useState } from 'react'
import ApexCharts from 'apexcharts'
import './StackedBarChart.css'

interface StackedBarChartProps {
    categories: string[]
    series: { name: string; data: number[] }[]
    colors?: string[]
    height?: number | string
    yAxisMax?: number
    yAxisMin?: number
    yAxisTickAmount?: number
    yAxisLabel?: string
    yAxisWidth?: number
    yAxisShow?: boolean
    xAxisShow?: boolean
    showGrid?: boolean
    showDataLabels?: boolean
    dataLabelColors?: string[]
    dataLabelMinPercent?: number
    dataLabelFontSize?: string
    sparkline?: boolean
    columnWidth?: string
    fontFamily?: string
    xAxisLabelFontFamily?: string
    xAxisLabelFontSize?: string
    xAxisLabelFontWeight?: string
    exportFilename?: string
    isLoading?: boolean
    noDataMessage?: string
    fillOpacity?: number
    tooltipFixed?: boolean
    gridPaddingBottom?: number
    hideTooltipTitle?: boolean
    yAxisTickPercent?: boolean
    xAxisLabelOffsetX?: number
    showXAxisLabels?: boolean
}

const DEFAULT_COLORS = ['#429c75', '#c66736']

const StackedBarChart = React.forwardRef<any, StackedBarChartProps>((
    {
        categories,
        series,
        colors = DEFAULT_COLORS,
        height = 210,
        yAxisMax = 100,
        yAxisMin = 0,
        yAxisTickAmount = 5,
        yAxisLabel = '',
        yAxisWidth,
        yAxisShow = true,
        xAxisShow = true,
        showGrid = true,
        showDataLabels = false,
        dataLabelColors,
        dataLabelMinPercent,
        dataLabelFontSize = '20px',
        sparkline = false,
        columnWidth = '55%',
        fontFamily = 'Helvetica, Arial, sans-serif',
        xAxisLabelFontFamily,
        xAxisLabelFontSize,
        xAxisLabelFontWeight,
        exportFilename,
        isLoading = false,
        noDataMessage = 'No data available for selected hospital(s)',
        fillOpacity,
        tooltipFixed = false,
    gridPaddingBottom,
    hideTooltipTitle = false,
    yAxisTickPercent = false,
    xAxisLabelOffsetX,
    showXAxisLabels = true,
    },
    ref
) => {
    const chartRef = useRef<HTMLDivElement>(null)
    const chartInstance = useRef<ApexCharts | null>(null)
    const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())

    React.useImperativeHandle(ref, () => ({
        exportToSVG: () => {
            chartInstance.current?.exports?.exportToSVG()
        },
        exportToCSV: () => {
            chartInstance.current?.exports?.exportToCSV({
                series: undefined,
                columnDelimiter: ','
            })
        },
    }))

    useEffect(() => {
        if (!chartRef.current) return

        const options: ApexCharts.ApexOptions = {
            chart: {
                type: 'bar',
                height,
                stacked: true,
                fontFamily,
                background: 'transparent',
                parentHeightOffset: 0,
                sparkline: { enabled: sparkline },
                toolbar: { show: false },
            },
            fill: {
                type: 'solid',
                opacity: fillOpacity ?? 0.85,
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth,
                    borderRadius: 0,
                },
            },
            series,
            xaxis: {
                categories: categories.map(c => c.includes('\n') ? c.split('\n') : c),
                labels: {
                    show: showXAxisLabels,
                    rotate: 0,
                    trim: false,
                    hideOverlappingLabels: false,
                    offsetX: xAxisLabelOffsetX ?? 0,
                    style: {
                        fontSize: xAxisLabelFontSize ?? '10px',
                        fontFamily: xAxisLabelFontFamily ?? fontFamily,
                        fontWeight: xAxisLabelFontWeight,
                        colors: '#000000',
                    },
                },
                axisBorder: { show: xAxisShow },
                axisTicks: { show: xAxisShow },
            },
            yaxis: {
                show: yAxisShow,
                min: yAxisMin,
                max: yAxisMax,
                tickAmount: yAxisTickAmount,
                title: {
                    text: yAxisLabel || '',
                    style: { fontSize: '14px', color: '#333', fontFamily },
                },
                labels: {
                    show: yAxisShow,
                    formatter: (val) => typeof val === 'number' ? `${Math.round(val)}${yAxisTickPercent ? '%' : ''}` : val,
                    style: {
                        fontSize: '12px',
                        fontFamily,
                        colors: '#000000',
                    },
                    minWidth: yAxisWidth,
                    maxWidth: yAxisWidth ? yAxisWidth + 50 : undefined,
                },
                axisBorder: { show: yAxisShow },
                axisTicks: { show: yAxisShow },
            },
            colors,
            dataLabels: {
                enabled: showDataLabels,
                formatter: (val) => {
                    const num = Number(val)
                    if (!isFinite(num) || num === 0) return ''
                    if (dataLabelMinPercent !== undefined && Math.round(num) < dataLabelMinPercent) return ''
                    return `${Math.round(num)}%`
                },
                style: {
                    fontSize: dataLabelFontSize,
                    fontWeight: '400',
                    fontFamily,
                    colors: dataLabelColors,
                },
                dropShadow: { enabled: false },
            },
            legend: { show: false },
            grid: {
                show: showGrid,
                borderColor: showGrid ? '#e0e0e0' : 'transparent',
                padding: { top: 0, right: 5, bottom: gridPaddingBottom ?? 0, left: 5 },
                xaxis: { lines: { show: showGrid } },
                yaxis: { lines: { show: showGrid } },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        chart: { height: typeof height === 'number' ? Math.max(height, 240) : height },
                        xaxis: {
                            labels: {
                                rotate: -45,
                                rotateAlways: true,
                                offsetY: 2,
                                hideOverlappingLabels: true,
                                style: {
                                    fontSize: '9px',
                                    fontFamily,
                                    colors: '#000000',
                                },
                            },
                        },
                        grid: {
                            padding: { top: 0, right: 4, bottom: 8, left: 4 },
                        },
                    },
                },
            ],
            stroke: { show: false, width: 0 },
            tooltip: {
                shared: false,
                intersect: true,
                fixed: tooltipFixed
                    ? { enabled: true, position: 'topRight', offsetX: 10, offsetY: 0 }
                    : { enabled: false },
                x: { show: !hideTooltipTitle },
                y: {
                    formatter: (val) => {
                        if (val === null || val === undefined || isNaN(val)) return '0'
                        return Math.round(val).toString() + '%'
                    },
                },
            },
            noData: {
                text: noDataMessage,
                align: 'center',
                verticalAlign: 'middle',
                offsetY: -45,
                style: { color: 'Black', fontSize: '12px', fontFamily },
            },
        }

        if (exportFilename) {
            options.chart!.toolbar = {
                show: false,
                export: {
                    csv: { filename: exportFilename },
                    svg: { filename: exportFilename },
                    png: { filename: exportFilename },
                },
            }
        }

        if (chartInstance.current) {
            chartInstance.current.updateOptions(options, true, true)
        } else {
            if (!chartRef.current) return
            chartInstance.current = new ApexCharts(chartRef.current, options)
            chartInstance.current.render()
        }
    }, [
        JSON.stringify(series),
        JSON.stringify(categories),
        height,
        JSON.stringify(colors),
        yAxisMax,
        yAxisMin,
        yAxisTickAmount,
        yAxisLabel,
        yAxisWidth,
        yAxisShow,
        xAxisShow,
        showGrid,
        showDataLabels,
        JSON.stringify(dataLabelColors),
        dataLabelMinPercent,
        dataLabelFontSize,
        columnWidth,
        fontFamily,
        xAxisLabelFontFamily,
        xAxisLabelFontSize,
        xAxisLabelFontWeight,
        exportFilename,
        noDataMessage,
        fillOpacity,
        tooltipFixed,
        gridPaddingBottom,
        hideTooltipTitle,
        yAxisTickPercent,
        xAxisLabelOffsetX,
        showXAxisLabels,
    ])

    useEffect(() => {
        const el = chartRef.current
        if (!el) return

        let timeout: ReturnType<typeof setTimeout>

        const observer = new ResizeObserver(() => {
            clearTimeout(timeout)
            timeout = setTimeout(() => {
                if (chartInstance.current) {
                    chartInstance.current.updateOptions({}, true, false)
                }
            }, 50)
        })

        observer.observe(el)
        // Also observe parent in case the scroll container changes
        if (el.parentElement) {
            observer.observe(el.parentElement)
        }
        return () => {
            observer.disconnect()
            clearTimeout(timeout)
        }
    }, [])

    useEffect(() => {
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy()
                chartInstance.current = null
            }
        }
    }, [])

    return (
        <div className="stacked-bar-chart-wrapper">
            <div className="stacked-bar-chart-body">
                <div className="stacked-bar-chart-container" style={{ position: 'relative' }}>
                    <div ref={chartRef} className="stacked-bar-chart" />
                    {isLoading && (
                        <div className="chart-loading-overlay">
                            <div>Loading...</div>
                        </div>
                    )}
                </div>
                <div className="stacked-bar-chart-legend">
                    {series.map((s, i) => {
                        const isHidden = hiddenSeries.has(s.name)
                        return (
                            <div
                                key={s.name}
                                className="stacked-bar-chart-legend-item"
                                style={{ cursor: 'pointer', opacity: isHidden ? 0.4 : 1 }}
                                onClick={() => {
                                    if (!chartInstance.current) return
                                    chartInstance.current.toggleSeries(s.name)
                                    setHiddenSeries(prev => {
                                        const next = new Set(prev)
                                        if (next.has(s.name)) {
                                            next.delete(s.name)
                                        } else {
                                            next.add(s.name)
                                        }
                                        return next
                                    })
                                }}
                            >
                                <span
                                    className="stacked-bar-chart-legend-dot"
                                    style={{ backgroundColor: colors[i] || DEFAULT_COLORS[i] }}
                                />
                                <span className="stacked-bar-chart-legend-label">{s.name}</span>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
})

StackedBarChart.displayName = 'StackedBarChart'

export default StackedBarChart
